import React, { useState, useRef, useEffect } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  ActivityIndicator,
  Dimensions,
  Platform,
} from 'react-native';
import Icon from 'react-native-vector-icons/MaterialIcons';
import { useTheme } from '../contexts/ThemeContext';
import { getColors } from '../styles/theme';

const FONT_FAMILY = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif';
const HEADER_HEIGHT = 56;
const BOTTOM_BAR_HEIGHT = 64;
// Extra space so the Confirm button sits above the app's bottom tab bar (not cut off)
const BOTTOM_SAFE_INSET = 88;

/**
 * ClubLocationPickerModal
 *
 * Full-screen overlay that lets a club owner tap / drag a pin
 * to choose exactly where their club marker should appear.
 */
const ClubLocationPickerModal = ({
  visible,
  initialLocation,
  deviceLocation,
  onConfirm,
  onDismiss,
  clubName,
}) => {
  const { isDarkMode } = useTheme();
  const colors = getColors(isDarkMode);
  const [pinLocation, setPinLocation] = useState(null);
  const [saving, setSaving] = useState(false);
  const [cursorPos, setCursorPos] = useState(null); // { x, y } for dashed drop circle
  const mapPickerRef = useRef(null);
  const googleMapRef = useRef(null);
  const markerRef = useRef(null);

  // Set initial pin when modal opens
  useEffect(() => {
    if (visible) {
      if (initialLocation?.latitude && initialLocation?.longitude) {
        setPinLocation(initialLocation);
      } else if (deviceLocation?.latitude && deviceLocation?.longitude) {
        setPinLocation(deviceLocation);
      }
    }
  }, [visible, initialLocation, deviceLocation]);

  const handleConfirm = async () => {
    if (!pinLocation) return;
    setSaving(true);
    try {
      await onConfirm(pinLocation);
    } finally {
      setSaving(false);
    }
  };

  const handleUseMyLocation = () => {
    if (deviceLocation?.latitude && deviceLocation?.longitude) {
      setPinLocation({ ...deviceLocation });
      if (googleMapRef.current) {
        googleMapRef.current.panTo({
          lat: deviceLocation.latitude,
          lng: deviceLocation.longitude,
        });
      }
    }
  };

  if (!visible) return null;

  // ─── Web implementation ───
  if (Platform.OS === 'web') {
    const center = pinLocation || deviceLocation || { latitude: 43.6532, longitude: -79.3832 };
    const bg = isDarkMode ? '#1A1A1A' : '#FFFFFF';
    const borderClr = isDarkMode ? '#333' : '#E0E0E0';

    return (
      <div
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 99999,
          fontFamily: FONT_FAMILY,
          display: 'flex',
          flexDirection: 'column',
          background: isDarkMode ? '#121212' : '#f5f5f5',
        }}
      >
        {/* ── Header ── */}
        <div
          style={{
            height: HEADER_HEIGHT,
            minHeight: HEADER_HEIGHT,
            background: bg,
            display: 'flex',
            alignItems: 'center',
            padding: '0 16px',
            gap: 12,
            borderBottom: `1px solid ${borderClr}`,
            boxShadow: isDarkMode
              ? '0 2px 8px rgba(0,0,0,0.5)'
              : '0 2px 8px rgba(0,0,0,0.08)',
            zIndex: 2,
            flexShrink: 0,
          }}
        >
          {/* Close */}
          <div
            onClick={onDismiss}
            style={{
              width: 36,
              height: 36,
              borderRadius: 10,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              background: isDarkMode ? '#2D2D2D' : '#F0F0F0',
              flexShrink: 0,
              transition: 'background 0.15s',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = isDarkMode ? '#3A3A3A' : '#E0E0E0')}
            onMouseLeave={(e) => (e.currentTarget.style.background = isDarkMode ? '#2D2D2D' : '#F0F0F0')}
          >
            <Icon name="close" size={20} color={colors.textSecondary} />
          </div>

          {/* Title */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontSize: 16,
                fontWeight: 700,
                color: colors.text,
                lineHeight: '20px',
                fontFamily: FONT_FAMILY,
              }}
            >
              Set Club Location
            </div>
            <div
              style={{
                fontSize: 12,
                color: colors.textSecondary,
                lineHeight: '16px',
                fontFamily: FONT_FAMILY,
              }}
            >
              Tap the map to place your marker
            </div>
          </div>

          {/* Inline help pill */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              background: isDarkMode ? 'rgba(25,118,210,0.15)' : '#E3F2FD',
              borderRadius: 20,
              padding: '6px 14px',
              flexShrink: 0,
            }}
          >
            <Icon name="info-outline" size={14} color={isDarkMode ? '#64B5F6' : '#1976D2'} />
            <span
              style={{
                fontSize: 11,
                fontWeight: 500,
                color: isDarkMode ? '#90CAF9' : '#1565C0',
                fontFamily: FONT_FAMILY,
                whiteSpace: 'nowrap',
              }}
            >
              Won't move with your device
            </span>
          </div>
        </div>

        {/* ── Map (fills all remaining space) ── */}
        <div
          style={{
            flex: '1 1 0%',
            position: 'relative',
            minHeight: 0,
          }}
          onMouseMove={(e) => setCursorPos({ x: e.clientX, y: e.clientY })}
          onMouseLeave={() => setCursorPos(null)}
        >
          <div
            ref={mapPickerRef}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
            }}
          />

          {/* Dashed circle at cursor to show drop zone */}
          {cursorPos && (
            <div
              style={{
                position: 'fixed',
                left: cursorPos.x,
                top: cursorPos.y,
                width: 48,
                height: 48,
                marginLeft: -24,
                marginTop: -24,
                borderRadius: '50%',
                border: `2px dashed ${colors.primary}`,
                pointerEvents: 'none',
                zIndex: 10,
                opacity: 0.9,
              }}
            />
          )}

          {/* Crosshair prompt when no pin yet */}
          {!pinLocation && (
            <div
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                pointerEvents: 'none',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <Icon name="add-location" size={44} color={colors.primary} />
              <div
                style={{
                  background: 'rgba(0,0,0,0.65)',
                  color: '#fff',
                  padding: '5px 12px',
                  borderRadius: 8,
                  fontSize: 12,
                  fontWeight: 500,
                  fontFamily: FONT_FAMILY,
                  whiteSpace: 'nowrap',
                  backdropFilter: 'blur(4px)',
                }}
              >
                Tap to place marker
              </div>
            </div>
          )}
        </div>

        {/* ── Bottom Action Bar (padded above tab bar so Confirm is tappable) ── */}
        <div
          style={{
            minHeight: BOTTOM_BAR_HEIGHT,
            paddingTop: 12,
            paddingBottom: BOTTOM_SAFE_INSET,
            paddingLeft: 16,
            paddingRight: 16,
            background: bg,
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            borderTop: `1px solid ${borderClr}`,
            boxShadow: isDarkMode
              ? '0 -2px 8px rgba(0,0,0,0.5)'
              : '0 -2px 8px rgba(0,0,0,0.06)',
            zIndex: 2,
            flexShrink: 0,
          }}
        >
          {/* Use My Location */}
          {deviceLocation && (
            <button
              onClick={handleUseMyLocation}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '9px 14px',
                borderRadius: 10,
                border: `1px solid ${borderClr}`,
                background: isDarkMode ? '#2D2D2D' : '#F8F9FA',
                fontSize: 13,
                fontWeight: 600,
                color: colors.text,
                cursor: 'pointer',
                fontFamily: FONT_FAMILY,
                transition: 'background 0.15s',
                outline: 'none',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = isDarkMode ? '#383838' : '#E9ECEF')}
              onMouseLeave={(e) => (e.currentTarget.style.background = isDarkMode ? '#2D2D2D' : '#F8F9FA')}
            >
              <Icon name="my-location" size={16} color={colors.primary} />
              <span>Use My Location</span>
            </button>
          )}

          <div style={{ flex: 1 }} />

          {/* Confirm */}
          <button
            onClick={!saving && pinLocation ? handleConfirm : undefined}
            disabled={!pinLocation || saving}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 7,
              padding: '9px 20px',
              borderRadius: 10,
              border: 'none',
              background: pinLocation ? colors.primary : (isDarkMode ? '#333' : '#ccc'),
              fontSize: 14,
              fontWeight: 700,
              color: '#fff',
              cursor: pinLocation && !saving ? 'pointer' : 'not-allowed',
              opacity: pinLocation && !saving ? 1 : 0.5,
              fontFamily: FONT_FAMILY,
              transition: 'all 0.15s',
              outline: 'none',
              boxShadow: pinLocation
                ? `0 3px 10px ${isDarkMode ? 'rgba(127,176,105,0.4)' : 'rgba(127,176,105,0.3)'}`
                : 'none',
            }}
          >
            {saving ? (
              <>
                <ActivityIndicator size="small" color="#fff" />
                <span>Saving...</span>
              </>
            ) : (
              <>
                <Icon name="check" size={18} color="#fff" />
                <span>Confirm Location</span>
              </>
            )}
          </button>
        </div>

        {/* Initialize Google Map */}
        <GoogleMapInit
          containerRef={mapPickerRef}
          googleMapRef={googleMapRef}
          markerRef={markerRef}
          center={center}
          pinLocation={pinLocation}
          setPinLocation={setPinLocation}
          isDarkMode={isDarkMode}
          colors={colors}
        />
      </div>
    );
  }

  // ─── Native fallback ───
  return null;
};

/**
 * Helper: initializes a Google Map inside the given container ref.
 */
const GoogleMapInit = ({
  containerRef,
  googleMapRef,
  markerRef,
  center,
  pinLocation,
  setPinLocation,
  isDarkMode,
  colors,
}) => {
  const initializedRef = useRef(false);

  useEffect(() => {
    if (!containerRef.current || initializedRef.current) return;
    if (typeof window === 'undefined' || !window.google?.maps) return;

    initializedRef.current = true;

    const map = new window.google.maps.Map(containerRef.current, {
      center: { lat: center.latitude, lng: center.longitude },
      zoom: 15,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
      zoomControl: true,
      gestureHandling: 'greedy',
      styles: isDarkMode
        ? [
            { elementType: 'geometry', stylers: [{ color: '#242f3e' }] },
            { elementType: 'labels.text.stroke', stylers: [{ color: '#242f3e' }] },
            { elementType: 'labels.text.fill', stylers: [{ color: '#746855' }] },
            { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#38414e' }] },
            { featureType: 'road', elementType: 'geometry.stroke', stylers: [{ color: '#212a37' }] },
            { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#746855' }] },
            { featureType: 'road.highway', elementType: 'geometry.stroke', stylers: [{ color: '#1f2835' }] },
            { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#17263c' }] },
            { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#515c6d' }] },
            { featureType: 'poi', elementType: 'labels.text.fill', stylers: [{ color: '#d59563' }] },
            { featureType: 'transit', elementType: 'geometry', stylers: [{ color: '#2f3948' }] },
          ]
        : [],
    });

    googleMapRef.current = map;

    // Place existing pin
    if (pinLocation) {
      placeMarker(map, markerRef, pinLocation, setPinLocation, colors);
    }

    // Click to place
    map.addListener('click', (e) => {
      const newLoc = {
        latitude: e.latLng.lat(),
        longitude: e.latLng.lng(),
      };
      setPinLocation(newLoc);
      placeMarker(map, markerRef, newLoc, setPinLocation, colors);
    });
  }, [containerRef.current]);

  // Sync marker when pinLocation changes externally
  useEffect(() => {
    if (!googleMapRef.current || !pinLocation || !initializedRef.current) return;
    placeMarker(googleMapRef.current, markerRef, pinLocation, setPinLocation, colors);
  }, [pinLocation?.latitude, pinLocation?.longitude]);

  return null;
};

function placeMarker(map, markerRef, location, setPinLocation, colors) {
  if (markerRef.current) {
    markerRef.current.setMap(null);
  }

  const marker = new window.google.maps.Marker({
    position: { lat: location.latitude, lng: location.longitude },
    map,
    draggable: true,
    icon: {
      path: window.google.maps.SymbolPath.CIRCLE,
      scale: 14,
      fillColor: colors.primary,
      fillOpacity: 1,
      strokeColor: '#fff',
      strokeWeight: 3,
    },
    title: 'Club Location',
  });

  marker.addListener('dragend', (e) => {
    setPinLocation({
      latitude: e.latLng.lat(),
      longitude: e.latLng.lng(),
    });
  });

  markerRef.current = marker;
}

export default ClubLocationPickerModal;
